import React, { useState } from 'react';
import { Modal, Button, FormField, Input, Select, PageHeader } from '../../components/UI';
import Breadcrumb from '../../components/Breadcrumb';
import FundingSourceModal from '../../components/FundingSourceModal';
import { expenseService } from '../../api/services';
import { formatCurrency } from '../../utils/helpers';
import { toJalali } from '../../utils/jalali';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import ConfirmModal from '../../components/ConfirmModal';
import toast from 'react-hot-toast';

const CATS = ['Rent','Utilities','Salaries','Equipment','Supplies','Maintenance','Marketing','Transportation','Supplier Payment','Partner Repayment','Other'];
const FUNDING_LABELS = {
  pharmacy_cash:'Pharmacy Cash',
  partner_halim:'Abdul Halim Adib Karimi',
  partner_nuroollah:'Nuroollah Mansoor',
};

export default function ExpensesPage() {
  const [data,setData]=useState([]);const [total,setTotal]=useState(0);const [loading,setLoading]=useState(false);
  const [page,setPage]=useState(1);const [saving,setSaving]=useState(false);const [deleting,setDeleting]=useState(false);
  const [fundingOpen,setFundingOpen]=useState(false);const [formOpen,setFormOpen]=useState(false);
  const [deleteTarget,setDelTarget]=useState(null);const [editItem,setEditItem]=useState(null);
  const [form,setForm]=useState({category:'',amount:'',description:'',date:'',fundingSource:'pharmacy_cash',paidBy:'Pharmacy Cash'});

  React.useEffect(()=>{fetchData();},[page]); // eslint-disable-line

  const fetchData=async()=>{
    setLoading(true);
    try{const r=await expenseService.getAll({page,limit:20});setData(r.data.data||[]);setTotal(r.data.pagination?.total||0);}
    catch{toast.error('Failed');}finally{setLoading(false);}
  };

  const handleSave=async()=>{
    if(!form.category||!form.amount)return toast.error('Category and amount required');
    setSaving(true);
    try{
      if(editItem){await expenseService.update(editItem._id,form);toast.success('Updated');}
      else{await expenseService.create(form);toast.success('Recorded');}
      setFormOpen(false);setEditItem(null);
      setForm({category:'',amount:'',description:'',date:'',fundingSource:'pharmacy_cash',paidBy:'Pharmacy Cash'});
      fetchData();
    }catch(e){toast.error(e.response?.data?.message||'Failed');}finally{setSaving(false);}
  };

  const handleDelete=async()=>{
    setDeleting(true);
    try{await expenseService.delete(deleteTarget._id);toast.success('Deleted');setDelTarget(null);fetchData();}
    catch{toast.error('Failed');}finally{setDeleting(false);}
  };

  return(
    <div>
      <Breadcrumb/>
      <PageHeader title="Expenses" subtitle="Track and manage expenses"
        actions={<Button onClick={()=>setFundingOpen(true)}><Plus size={16}/> Add Expense</Button>}/>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100"><tr>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Category</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Description</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Funded By</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Amount</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {loading?Array(5).fill(0).map((_,i)=><tr key={i}><td colSpan={6} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td></tr>)
            :data.length===0?<tr><td colSpan={6} className="text-center py-12 text-gray-400">No expenses</td></tr>
            :data.map(row=>(
              <tr key={row._id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{row.category}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{row.description||'-'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${row.fundingSource&&row.fundingSource!=='pharmacy_cash'?'bg-purple-50 text-purple-700':'bg-blue-50 text-blue-700'}`}>
                    {FUNDING_LABELS[row.fundingSource]||row.paidBy||'Pharmacy Cash'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-semibold">{formatCurrency(row.amount)}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{toJalali(row.date||row.createdAt)}</td>
                <td className="px-4 py-3 text-center"><div className="flex justify-center gap-1">
                  <button onClick={()=>{setEditItem(row);setForm({category:row.category||'',amount:row.amount||'',description:row.description||'',date:row.date?row.date.split('T')[0]:'',fundingSource:row.fundingSource||'pharmacy_cash',paidBy:FUNDING_LABELS[row.fundingSource]||'Pharmacy Cash'});setFormOpen(true);}} className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600"><Pencil size={14}/></button>
                  <button onClick={()=>setDelTarget(row)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"><Trash2 size={14}/></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {total>20&&<div className="flex justify-between px-4 py-3 border-t border-gray-100">
          <p className="text-xs text-gray-500">{((page-1)*20)+1}–{Math.min(page*20,total)} of {total}</p>
          <div className="flex gap-1">
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40">Prev</button>
            <button onClick={()=>setPage(p=>p+1)} disabled={page*20>=total} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40">Next</button>
          </div>
        </div>}
      </div>

      {/* STEP 1: Funding Source Modal */}
      <FundingSourceModal
        isOpen={fundingOpen}
        onClose={()=>setFundingOpen(false)}
        title="Select Funding Source"
        onSelect={(src)=>{
          setForm({category:'',amount:'',description:'',date:'',fundingSource:src.key,paidBy:src.label});
          setFundingOpen(false);
          setEditItem(null);
          setFormOpen(true);
        }}
      />

      {/* STEP 2: Expense Form */}
      <Modal isOpen={formOpen} onClose={()=>{setFormOpen(false);setEditItem(null);}} title={editItem?'Edit Expense':'Add Expense'} size="md">
        <div className="space-y-4">
          {!editItem&&(
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-xs text-blue-600">Funded by:</span>
              <span className="text-sm font-bold text-blue-800">{form.paidBy}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Category" required>
              <Select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
                <option value="">Select</option>
                {CATS.map(c=><option key={c} value={c}>{c}</option>)}
              </Select>
            </FormField>
            <FormField label="Amount" required>
              <Input type="number" step="0.01" min="0" value={form.amount}
                onChange={e=>setForm(p=>({...p,amount:e.target.value}))} placeholder="0.00"/>
            </FormField>
            <FormField label="Date">
              <Input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))}/>
            </FormField>
            {editItem&&(
              <FormField label="Funded By">
                <Select value={form.fundingSource} onChange={e=>setForm(p=>({...p,fundingSource:e.target.value,paidBy:FUNDING_LABELS[e.target.value]}))}>
                  {Object.entries(FUNDING_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                </Select>
              </FormField>
            )}
            <div className="col-span-2">
              <FormField label="Description">
                <textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Description"/>
              </FormField>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={()=>{setFormOpen(false);setEditItem(null);}}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{editItem?'Update':'Save'}</Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal isOpen={!!deleteTarget} onClose={()=>setDelTarget(null)}
        onConfirm={handleDelete} loading={deleting}
        title="Delete Expense" message={`Delete expense of ${formatCurrency(deleteTarget?.amount)}?`}
        confirmLabel="Delete"/>
    </div>
  );
}
